import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ToggleableProductAccordionSlider, {
    ToggleableProductAccordionSliderStyles,
} from "@store/Components/QuickOrder/ToggleableProductAccordionSlider";
import ProductPromotionTextBanner, {
    ProductPromotionTextBannerStyles,
} from "@store/Components/Store/ProductPromotionTextBanner";
import ProductAddToCartButton from "@store/Overrides/Components/ProductAddToCartButton";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@store/Overrides/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@store/Overrides/Components/ProductQuantityOrdered";
import { dispatchProductClickEvent } from "@store/Services/AnalyticsService";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { COLORS, TYPOGRAPHY } from "@store/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ExplodedViewProductRowStyles {
    viewProductLink?: LinkPresentationProps;
    loginLink?: LinkPresentationProps;
    productImageStyles?: ProductImageStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    rowStyles?: DataTableRowProps;
    productBrandStyles?: ProductBrandStyles;
    productPromotionTextBannerStyles?: ProductPromotionTextBannerStyles;
    relatedProductsContainer?: GridContainerProps;
    relatedProductsCarousel?: ToggleableProductAccordionSliderStyles;
    cardNumberNumber?: InjectableCss;
}

interface OwnProps {
    id: string;
    productNumber: string;
    productTitle: string;
    product: ProductModel;
    selectionCallback?: (sku: string) => void;
    index: string;
    qty: number;
    isPopup?: boolean;
}

const mapStateToProps = (state: StoreApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, "explodedViewItems", ownProps.product.id),
    signInUrl: getPageLinkByPageType(state, "SignInPage"),
    userName: state.context.session?.userName,
    selectedSubAssembly: state.store?.data?.explodedView?.selectedSubAssembly,
});

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const explodedViewProductRowStyles: ExplodedViewProductRowStyles = {
    productImageStyles: {
        img: {
            css: css`
                height: 55px;
                width: auto;
                border: 1px solid ${COLORS.border.base};
            `,
        },
    },
    rowStyles: {
        css: css`
            td {
                padding-top: 1em;
                padding-bottom: 1em;
            }
        `,
    },
    viewProductLink: {
        typographyProps: {
            underline: true,
            size: "15px",
        },
        hoverStyle: {
            textDecorationLine: "none",
        },
    },
    loginLink: {
        typographyProps: {
            underline: true,
            size: "15px",
        },
        hoverStyle: {
            textDecorationLine: "none",
        },
    },
    quantityBreakPricing: {
        viewLink: {},
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                margin-right: 1em;
                height: 40px;
                width: 50px;
            `,
            inputSelect: css`
                height: 40px;
                width: 50px;
                text-align: center;
            `,
        },
    },
    productBrandStyles: {
        nameLink: {
            typographyProps: {
                size: "15px",
                weight: 500,
            },
        },
        nameText: {
            size: "15px",
            weight: 500,
        },
    },
    productPromotionTextBannerStyles: {
        wrapper: {
            css: css`
                display: inline-block;
                position: static;
                z-index: 1;
                padding: 0.25em 0.5em;
                text-align: center;
                align-items: center;
                margin-bottom: 4px;
            `,
        },
        label: {
            size: TYPOGRAPHY.size.veryTiny,
            color: "white",
            css: css`
                display: block;
                font-weight: bold;
                text-transform: uppercase;
                margin-bottom: 0;
            `,
        },
    },
    relatedProductsContainer: {
        css: css`
            margin-top: 0.5em;
            max-width: 600px;
            min-width: 200px;
        `,
    },
    relatedProductsCarousel: {
        triggerActionWrapper: {
            css: css`
                zoom: 0.8;
                position: relative;
                display: block;
                max-width: 100%;
                background: ${COLORS.background.alt};
                border: 1px solid ${COLORS.border.base};
                margin-right: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css``,
                        css``,
                        css``,
                        css`
                            margin-left: 0;
                        `,
                        css`
                            margin-left: 0;
                        `,
                    ])}
            `,
        },
        sliderDisplayButton: {
            css: css`
                align-items: normal;
                display: flex;
                text-decoration: none;
                font-weight: bold;
                padding: 8px;
                padding-left: 12px;
                font-size: 16px;
                max-width: 100%;
                width: 100%;

                &,
                &:hover {
                    background: transparent;
                    border: 0;
                }

                &:hover {
                    text-decoration: underline;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                flex-direction: column;
                            `,
                            css``,
                            css`
                                flex-direction: column;
                            `,
                            css``,
                            css``,
                        ],
                        "min",
                    )}
            `,
        },
        thumbImg: {
            css: css`
                height: auto;
                width: 25px;
                margin: 0.25em;
                border: 1px solid ${COLORS.border.base};
            `,
        },
        carouselTopWrapper: {
            css: css`
                zoom: 0.8;
                background: ${COLORS.background.alt};
                border: 1px solid ${COLORS.border.base};
                border-top: 0;
                padding: 0.5em;
                display: block;
                max-width: 100%;
                width: 100%;
            `,
        },
        carouselSlide: {
            css: css`
                padding: 0;
                position: relative;
                flex: 0 0 auto;
                margin-left: 7.5px;
                margin-right: 7.5px;
                min-width: 245px;
                width: 85% !important;
            `,
        },
        carouselProductStyles: {
            productDescriptionStyles: {
                productDetailLink: {
                    css: css`
                        margin: 0;
                        font-weight: ${TYPOGRAPHY.weight.black};
                        font-size: ${TYPOGRAPHY.size.base};
                        white-space: normal;
                    `,
                },
                productDetailDescription: {
                    css: css`
                        margin: 0;
                        font-weight: ${TYPOGRAPHY.weight.black};
                        font-size: ${TYPOGRAPHY.size.base};
                        white-space: normal;
                    `,
                },
            },
        },
    },
    cardNumberNumber: {
        css: css`
            cursor: pointer;
            border-radius: 50%;
            width: 30px;
            height: 30px;
            line-height: 30px;
            display: block;
            font-weight: ${TYPOGRAPHY.weight.black};
            font-size: ${TYPOGRAPHY.size.smaller};
            color: ${getColor("secondary.contrast")};
            background: ${getColor("primary.contrast")};
            text-align: center;
        `,
    },
};

const styles = explodedViewProductRowStyles;

const ExplodedViewProductRow: React.FC<Props> = ({
    id,
    productNumber,
    productTitle,
    productInfo,
    signInUrl,
    userName,
    product,
    updateProductInfo,
    selectionCallback,
    index,
    qty,
    isPopup,
    selectedSubAssembly,
}) => {
    const rowRef = React.useRef<HTMLDivElement>(null);
    if (!product || !productInfo) {
        return null;
    }
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: "explodedViewItems", productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: "explodedViewItems", productId: product.id, unitOfMeasure });
    };
    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const handleAnalyticsClick = () => {
        dispatchProductClickEvent(productContext);
    };
    const handleSelectionClick = () => {
        if (selectionCallback) {
            selectionCallback(id);
        }
    };

    const generateCardStylesWithSelection = (selected: boolean) => ({
        rowStyles: {
            css: css`
                ${selected ? `border: 2px solid ${COLORS.primary.base}` : ""};
                td {
                    padding-top: 1em;
                    padding-bottom: 1em;
                }
            `,
        },
    });

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <DataTableRow
                    onClick={handleSelectionClick}
                    data-sku={`${id}`}
                    id={`${id}_${index}`}
                    {...generateCardStylesWithSelection(selectedSubAssembly === id).rowStyles}
                >
                    <DataTableCell>
                        <StyledWrapper>
                            <Typography {...styles.cardNumberNumber}>{index}</Typography>
                        </StyledWrapper>
                    </DataTableCell>
                    <DataTableCell>
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </DataTableCell>
                    <DataTableCell>
                        <div>
                            <ProductPromotionTextBanner
                                extendedStyles={styles.productPromotionTextBannerStyles}
                                product={productContext}
                            ></ProductPromotionTextBanner>
                        </div>
                        <Link
                            {...styles.viewProductLink}
                            href={productInfo?.productDetailPath}
                            onClick={handleAnalyticsClick}
                        >
                            {productTitle}
                        </Link>

                        <StyledWrapper {...styles.relatedProductsContainer}>
                            <ToggleableProductAccordionSlider
                                id={product.id + productInfo.unitOfMeasure}
                                product={productContext}
                                extendedStyles={styles.relatedProductsCarousel}
                                defaultIsOpen={true}
                                productsToDisplay={1}
                                productsToScroll={1}
                                isQuickOrder={false}
                            ></ToggleableProductAccordionSlider>
                        </StyledWrapper>
                    </DataTableCell>
                    <DataTableCell>
                        {product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )}
                    </DataTableCell>
                    <DataTableCell>{productNumber}</DataTableCell>
                    {userName ? (
                        <>
                            <DataTableCell>
                                <ProductQuantityOrdered
                                    qtyNeeded={qty || 1}
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    labelOverride=""
                                    id="explodedViewItems-qty"
                                />
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.quantityBreakPricing}
                                    isModal={true}
                                />
                            </DataTableCell>
                            <DataTableCell>
                                {productInfo?.pricing?.additionalResults?.priceMinusCorePrice
                                    ? productInfo?.pricing?.additionalResults?.priceMinusCorePrice
                                    : productInfo?.pricing?.unitRegularPriceDisplay}
                                {productInfo?.unitOfMeasure && <>/{productInfo?.unitOfMeasure}</>}
                            </DataTableCell>
                        </>
                    ) : (
                        <DataTableCell>
                            <Link {...styles.loginLink} href={signInUrl?.url}>
                                {translate("Please log in to get prices")}
                            </Link>
                        </DataTableCell>
                    )}
                    <DataTableCell>
                        <ProductAddToCartButton
                            data-test-selector="addToCartBtn"
                            analyticsId="ExplodedViewProductRow"
                        />
                    </DataTableCell>
                </DataTableRow>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ExplodedViewProductRow);
